"use client";

import { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import { Copy, Trash2, ExternalLink, Users, Camera } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ChannelQr } from "./channel-qr";
import type { CapturedChat, NotificationChannelView } from "@/hooks/notifications/use-notification-channels";
import { useTranslations } from "next-intl"
import { TranslationText } from "@/components/i18n/translation-text"





interface TelegramEnrollmentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  channel: NotificationChannelView | null;
  onRemoveChat: (chatId: string) => Promise<void>;
}

function readChats(channel: NotificationChannelView | null): CapturedChat[] {
  const raw = (channel?.config as Record<string, unknown> | undefined)?.chats;
  if (!Array.isArray(raw)) return [];
  return raw.filter((c): c is CapturedChat => !!c && typeof (c as CapturedChat).id === "string");
}

export function TelegramEnrollmentDialog({
  open,
  onOpenChange,
  channel,
  onRemoveChat,
}: TelegramEnrollmentDialogProps) {


  const tUi = useTranslations("NotificationChannels.NotificationChannelsTelegramEnrollmentDialog")

  const [removingId, setRemovingId] = useState<string | null>(null);

  const config = (channel?.config ?? {}) as Record<string, unknown>;
  const botUsername = typeof config.bot_username === "string" ? config.bot_username : null;
  const pairingToken = typeof config.pairing_token === "string" ? config.pairing_token : null;
  const chats = readChats(channel);

  // The parent polls while this dialog is open, so a chat captured server-side
  // appears here on its own. Confirm it to the user the moment the count grows.
  const prevChatCount = useRef<number | null>(null);
  useEffect(() => {
    if (!open) {
      prevChatCount.current = null;
      return;
    }
    if (prevChatCount.current !== null && chats.length > prevChatCount.current) {
      toast.success(chats.length - prevChatCount.current === 1 ? "New chat enrolled" : "New chats enrolled");
    }
    prevChatCount.current = chats.length;
  }, [open, chats.length]);

  const deepLink =
    botUsername && pairingToken
      ? `https://t.me/${botUsername}?start=${pairingToken}`
      : null;
  const groupLink =
    botUsername && pairingToken
      ? `https://t.me/${botUsername}?startgroup=${pairingToken}`
      : null;

  const copy = async (value: string, label: string) => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success(tUi("value0Copied", { value0: String(label) }));
    } catch {
      toast.error(tUi("couldNotCopyToClipboard"));
    }
  };

  const handleRemove = async (chatId: string) => {
    setRemovingId(chatId);
    try {
      await onRemoveChat(chatId);
      toast.success(tUi("chatRemoved"));
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to remove chat");
    } finally {
      setRemovingId(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle><span><span key={tUi("telegramEnrollment")}>{tUi("telegramEnrollment")}</span></span> {channel?.name}</DialogTitle>
          <DialogDescription>
            <span><span key={tUi("tap")}>{tUi("tap")}</span></span> <span className="font-medium"><span key={tUi("openInTelegram")}>{tUi("openInTelegram")}</span></span><span><span key={tUi("thenPress")}>{tUi("thenPress")}</span></span>{" "}
            <span className="font-medium"><span key={tUi("start")}>{tUi("start")}</span></span> <span><span key={tUi("noCodesToTypeShareTheLinkOr")}>{tUi("noCodesToTypeShareTheLinkOr")}</span></span>
          </DialogDescription>
        </DialogHeader>

        {deepLink ? (
          <div className="space-y-4">
            <div className="flex flex-col items-center gap-3">
              <ChannelQr value={deepLink} />
              <div className="flex max-w-sm items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-900/50 dark:bg-amber-950/40 dark:text-amber-200">
                <Camera className="mt-0.5 h-4 w-4 shrink-0" />
                <span><TranslationText>{tUi.rich("scanWithYourPhoneSTag0Tag1Camera", { tag0: chunks => (<span className="font-semibold">{chunks}</span>), tag1: chunks => (<span>{chunks}</span>) })}</TranslationText></span>
              </div>
              <div className="flex flex-wrap items-center justify-center gap-2">
                <Button size="sm" asChild>
                  <a href={deepLink} target="_blank" rel="noopener noreferrer">
                    <ExternalLink className="mr-1.5 h-3.5 w-3.5" /> <span><span key={tUi("openInTelegram")}>{tUi("openInTelegram")}</span></span>
                  </a>
                </Button>
                <Button size="sm" variant="outline" onClick={() => copy(deepLink, "Link")}>
                  <Copy className="mr-1.5 h-3.5 w-3.5" /> <span><span key={tUi("copyLink")}>{tUi("copyLink")}</span></span>
                </Button>
              </div>
            </div>

            {groupLink && (
              <div className="flex flex-wrap items-center justify-between gap-2 rounded-md bg-muted/50 px-3 py-2">
                <p className="min-w-[12rem] flex-1 text-xs text-muted-foreground">
                  <span key={tUi("haveATeamAddTheBotToA")}>{tUi("haveATeamAddTheBotToA")}</span></p>
                <Button size="sm" variant="outline" asChild>
                  <a href={groupLink} target="_blank" rel="noopener noreferrer">
                    <Users className="mr-1.5 h-3.5 w-3.5" /> <span><span key={tUi("addToAGroup")}>{tUi("addToAGroup")}</span></span>
                  </a>
                </Button>
              </div>
            )}

            <div className="space-y-2">
              <div className="flex items-center justify-between gap-2">
                <p className="text-sm font-medium">
                  <span><span key={tUi("enrolledChats")}>{tUi("enrolledChats")}</span></span> <span>{chats.length > 0 && `(${chats.length})`}</span>
                </p>
                <span className="text-xs text-muted-foreground"><span key={tUi("updatesAutomatically")}>{tUi("updatesAutomatically")}</span></span>
              </div>
              {chats.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  <span key={tUi("noChatsYetOpenTheLinkInTelegram")}>{tUi("noChatsYetOpenTheLinkInTelegram")}</span></p>
              ) : (
                <ul className="divide-y rounded-md border">
                  {chats.map((chat) => (
                    <li key={chat.id} className="flex items-center justify-between gap-2 px-3 py-2">
                      <span className="min-w-0 truncate text-sm">{chat.label || chat.id}</span>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleRemove(chat.id)}
                        disabled={removingId === chat.id}
                        aria-label={tUi("removeValue0", { value0: String(chat.label || chat.id) })}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">
            <span key={tUi("thisChannelIsMissingItsEnrollmentLinkReconnect")}>{tUi("thisChannelIsMissingItsEnrollmentLinkReconnect")}</span></p>
        )}
      </DialogContent>
    </Dialog>
  );
}
